import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui'
import { quietly, recordError } from '@/services/feedbackContext/capture'

function describeRouteError(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`.trim()
  if (error instanceof Error) return error.message
  return String(error)
}

/**
 * Root-route errorElement (SA-235). Renders OUTSIDE App's providers, so it
 * must stay dependency-light: ui Button + lucide + semantic tokens only.
 * The home link is a plain anchor on purpose — the motivating crash class
 * (translate/extension DOM rewrites) leaves the document in an unknown
 * state, so recovery is always a full document load.
 */
export function AppErrorBoundary() {
  const error = useRouteError()

  useEffect(() => {
    // SA-1304: the hit goes into the feedback context once, as the boundary's.
    recordError('boundary', describeRouteError(error))
    // Log the original object so the stack survives to the console.
    quietly(() => console.error('AppErrorBoundary caught a route error:', error))
  }, [error])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sorry — the page hit an unexpected error. Reloading usually fixes it, and your data is
          safe.
        </p>
        <p className="mt-4 text-xs text-muted-foreground font-mono break-words bg-muted rounded-md px-3 py-2">
          {describeRouteError(error)}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <a
            href="/"
            className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            Back to home
          </a>
        </div>
      </div>
    </div>
  )
}
